/**
 * DevTools Shield
 * Phòng vệ và triệt tiêu lỗi nội bộ của Chrome DevTools / Soft Navigation tracker:
 * "Uncaught TypeError: Cannot read properties of undefined (reading 'startTime') at et.reportAllChanges"
 *
 * Lỗi này xảy ra khi DevTools mở và script web-vitals nội bộ của Chrome chạy trong requestIdleCallback,
 * truy cập entry.startTime mà không có guard clause khi DOM unmount nhanh.
 */

export function isChromeDevToolsBug(err: any): boolean {
  if (!err) return false;
  let msg = "";
  if (typeof err === "string") {
    msg = err;
  } else if (err.message) {
    msg = String(err.message);
  } else if (err.description) {
    msg = String(err.description);
  } else {
    msg = String(err);
  }
  return msg.includes("reading 'startTime'") || msg.includes("reportAllChanges");
}

export function installDevToolsShield(targetWindow: any = typeof window !== "undefined" ? window : null) {
  if (!targetWindow) return;

  // 1. Bọc requestIdleCallback (Realm-proof, không dùng instanceof)
  if (typeof targetWindow.requestIdleCallback === "function") {
    const originalRequestIdleCallback = targetWindow.requestIdleCallback;
    targetWindow.requestIdleCallback = function (
      callback: (deadline: any) => void,
      options?: any
    ) {
      return originalRequestIdleCallback.call(
        targetWindow,
        function (deadline: any) {
          try {
            return callback(deadline);
          } catch (err: any) {
            if (isChromeDevToolsBug(err)) {
              // Hấp thụ nhẹ nhàng ngoại lệ từ script nội bộ của Chrome DevTools
              return;
            }
            throw err;
          }
        },
        options
      );
    };
  }

  // 2. Bọc setTimeout (chặn fallback timer n.timeout của Chrome khi rIC timeout)
  if (typeof targetWindow.setTimeout === "function") {
    const originalSetTimeout = targetWindow.setTimeout;
    targetWindow.setTimeout = function (fn: any, delay?: number, ...args: any[]) {
      if (typeof fn === "function") {
        const safeFn = function (this: any, ...callArgs: any[]) {
          try {
            return fn.apply(this, callArgs);
          } catch (err: any) {
            if (isChromeDevToolsBug(err)) {
              return;
            }
            throw err;
          }
        };
        return originalSetTimeout.call(targetWindow, safeFn, delay, ...args);
      }
      return originalSetTimeout.apply(targetWindow, [fn, delay, ...args]);
    };
  }

  // 3. window.onerror level 0 (trả về true báo cho trình duyệt đã xử lý, không in đỏ lên Console)
  const originalOnError = targetWindow.onerror;
  targetWindow.onerror = function (msg: any, url: any, lineNo: any, colNo: any, error: any) {
    if (isChromeDevToolsBug(msg) || isChromeDevToolsBug(error)) {
      return true;
    }
    if (typeof originalOnError === "function") {
      return originalOnError.call(targetWindow, msg, url, lineNo, colNo, error);
    }
    return false;
  };

  // 4. Bộ lắng nghe bắt lỗi toàn cục trong pha capture
  if (typeof targetWindow.addEventListener === "function") {
    targetWindow.addEventListener(
      "error",
      function (event: any) {
        if (
          isChromeDevToolsBug(event) ||
          (event && isChromeDevToolsBug(event.message)) ||
          (event && isChromeDevToolsBug(event.error))
        ) {
          if (typeof event.preventDefault === "function") {
            event.preventDefault();
          }
          if (typeof event.stopImmediatePropagation === "function") {
            event.stopImmediatePropagation();
          }
          return true;
        }
      },
      true
    );

    // 5. Bắt unhandled promise rejections nếu Chrome DevTools chạy trong async context
    targetWindow.addEventListener(
      "unhandledrejection",
      function (event: any) {
        if (isChromeDevToolsBug(event?.reason)) {
          if (typeof event.preventDefault === "function") {
            event.preventDefault();
          }
          return true;
        }
      },
      true
    );
  }
}
